import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Close as CloseIcon } from '@mui/icons-material';
import { selectSearchAndFilter, setColumnFilters } from 'app/store/filtering/searchAndFilterSlice';
import { applyTransactionColumnFilters } from 'app/store/filtering/transactionFiltersThunks';
import { ColumnFilter } from 'util/filtering/models';
import { useAppDispatch, useAppSelector } from 'app/store';
import theme from 'app/theme';
import { BootstrapInput, StyledPopper, StyledTextField } from './styles';
import { useLocation, useNavigate } from 'react-router-dom';

export const TextInput = (
  params: AutocompleteRenderInputParams,
  length: number,
  filter: ColumnFilter,
  onClickCloseButton: () => void,
  inputRef: React.Ref<HTMLInputElement>,
  onFocus: () => void,
  onClickLinkButton: () => void
) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleFocus = () => {
    onFocus();
  };

  return (
    <StyledTextField
      {...params}
      listLength={length}
      placeholder={filter.name}
      variant="standard"
      inputRef={inputRef}
      InputProps={{
        ...params.InputProps,
        startAdornment:
          length || filter.minNumber || filter.maxNumber ? (
            <InputAdornment onClick={onClickCloseButton} sx={{ cursor: 'pointer' }} position="start">
              <CloseIcon fontSize="small" />
            </InputAdornment>
          ) : null,
        endAdornment:
          length || filter.minNumber || filter.maxNumber ? (
            <InputAdornment onClick={onClickLinkButton} position="end" sx={{ fontWeight: 'bold' }}>
              <Chip
                color="info"
                label={
                  length ||
                  (filter.minNumber && filter.maxNumber ? 2 : filter.minNumber || filter.maxNumber ? 1 : 0)
                }
                size="small"
              />
            </InputAdornment>
          ) : null,
        disableUnderline: true,
        onBlur: handleBlur,
        onFocus: () => {
          handleFocus();
          setIsFocused(true);
        },
        style: {
          width: '100%',
          backgroundColor: '#FFFFFF',
          padding: '8px 12px',
          borderRadius: '8px',
          paddingLeft: length ? '12px' : '40px',
          border: isFocused ? '1px solid #0B7EB5' : 'none',
        },
      }}
    />
  );
};

const SelectOptionComponent = ({
  props,
  option,
  state,
  onFilterRuleChange,
  filterKey,
  onClose,
  filterRule,
}: any) => {
  // returns the select component in the first iteration
  return props['data-option-index'] === 0 ? (
    <>
      <li key={`select-${option.id}`}>
        <Select
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          input={<BootstrapInput />}
          sx={{ borderRadius: 0, '& :focus': { border: 'none', boxShadow: 'none !important' } }}
          fullWidth
          onChange={(e) => onFilterRuleChange(e, filterKey)}
          defaultValue="noExcept"
          onClose={onClose}
          value={filterRule}
        >
          <MenuItem value={'noExcept'}>
            <Typography>
              <b>No</b> [header] except
            </Typography>
          </MenuItem>
          <MenuItem value={'allExcept'}>
            <Typography>
              <b>All</b> [header] except
            </Typography>
          </MenuItem>
        </Select>
      </li>
      <li
        key={option.id}
        {...props}
        style={{
          backgroundColor: state.selected ? theme.palette.primary.main : '',
          color: state.selected ? '#FFF' : '',
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            paddingY: 0.8,
          }}
        >
          {option.label}
        </Box>
        <Box
          component={CheckCircleIcon}
          sx={{ width: 18, height: 18 }}
          style={{
            visibility: state.selected ? 'visible' : 'hidden',
          }}
        />
      </li>
    </>
  ) : (
    <li
      key={option.id}
      {...props}
      style={{
        backgroundColor: state.selected ? theme.palette.primary.main : '',
        color: state.selected ? '#FFF' : '',
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          paddingY: 0.8,
        }}
      >
        {option.label}
      </Box>
      <Box
        component={CheckCircleIcon}
        sx={{ width: 18, height: 18 }}
        style={{
          visibility: state.selected ? 'visible' : 'hidden',
        }}
      />
    </li>
  );
};

export interface ExistingValue {
  id: string;
  label: string;
}

interface DropdownFilterChipProps {
  filter: ColumnFilter;
  withMinWidth?: number;
}

export const DropdownFilterChip = ({ filter, withMinWidth }: DropdownFilterChipProps) => {
  const dispatch = useAppDispatch();
  const tableFilters = useAppSelector(selectSearchAndFilter)[filter.page];
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<ExistingValue[]>([]);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFilterRuleChange = (e: SelectChangeEvent, key: string) => {
    const changedFilterTable = { ...filter };
    changedFilterTable.rule = e.target.value;
    dispatch(
      setColumnFilters({
        pageName: filter.page,
        columnFilters: {
          [filter.key]: changedFilterTable,
        },
      })
    );
    focusAutocompleteTextField();
  };

  const handleSelectedValuesChange = (values: ExistingValue[], key: string) => {
    const changedFilterTable = { ...filter };
    changedFilterTable.selectedValues = values;
    changedFilterTable.minNumber = undefined;
    changedFilterTable.maxNumber = undefined;
    dispatch(
      applyTransactionColumnFilters({
        pageName: filter.page,
        columnFilters: {
          ...tableFilters.columnFilters,
          [filter.key]: changedFilterTable,
        },
      })
    );
  };

  const handleClearSelectedValues = () => {
    if (filter.key === 'receiptStatus') {
      const params = new URLSearchParams(location.search);
      if (params.has('filter')) {
        params.delete('filter');
        const qs = params.toString();
        const nextUrl = qs ? `${location.pathname}?${qs}` : location.pathname;
        navigate(nextUrl, { replace: true });
      }
    }

    handleSelectedValuesChange([], filter.key);
    setPendingValues([]);
    setOpen(false);
  };

  const focusAutocompleteTextField = () => {
    setTimeout(() => {
      if (inputRef?.current) {
        inputRef.current.focus();
      }
    }, 1);
  };

  const handleCloseSelect = () => {
    focusAutocompleteTextField();
  };

  const handleClickLinkButton = () => {
    setOpen(true);
  };

  const handleFocusTextField = () => {
    setOpen(true);
  };

  useEffect(() => {
    const selectedValues = tableFilters.columnFilters[filter.key].selectedValues;
    if (selectedValues && selectedValues.length > 0) {
      setPendingValues(selectedValues);
    } else {
      setPendingValues([]);
    }
  }, [tableFilters, filter]);

  return (
    <>
      <Autocomplete
        multiple
        readOnly
        onOpen={() => setOpen(true)}
        value={pendingValues}
        onChange={(event, newValues, reason) => setPendingValues(newValues)}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        inputValue={inputValue}
        onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
        id="job-codes-dropdown"
        renderOption={(props, option, state) => (
          <SelectOptionComponent
            key={`select-option-${option.id}`}
            filterKey={filter.key}
            props={props}
            option={option}
            state={state}
            onFilterRuleChange={handleFilterRuleChange}
            onClose={handleCloseSelect}
            filterRule={filter.rule}
          />
        )}
        disableCloseOnSelect
        options={filter.options || []}
        sx={{ minWidth: withMinWidth ?? 'auto' }}
        PopperComponent={StyledPopper}
        clearOnBlur
        openOnFocus
        renderInput={(params) =>
          TextInput(
            params,
            pendingValues.length,
            filter,
            handleClearSelectedValues,
            inputRef,
            handleFocusTextField,
            handleClickLinkButton
          )
        }
      />
    </>
  );
};
